import type { GeocoderResult } from "@phc/common"
import {
  AssetType,
  IndustryType,
  PlaceType,
  type MapboxLocationSchema as MapboxLocation,
} from "@phc/common/gen/preferencesGen"
import type { Asset, AssetInput } from "../preferencesApi"

const GLOBAL_ASSET_NAME = "PLANET_EARTH"

export const isAdminBoundaryFromPlaceType = (place_type?: string): boolean => {
  if (!place_type) return false
  switch (place_type as PlaceType) {
    case PlaceType.PLACE_TYPE_COUNTRY:
    case PlaceType.PLACE_TYPE_REGION:
    case PlaceType.PLACE_TYPE_POSTCODE:
    case PlaceType.PLACE_TYPE_DISTRICT:
      return true
    case PlaceType.PLACE_TYPE_ADDRESS:
    case PlaceType.PLACE_TYPE_LOCALITY:
    case PlaceType.PLACE_TYPE_NEIGHBORHOOD:
    case PlaceType.PLACE_TYPE_PLACE:
    case PlaceType.PLACE_TYPE_POI:
    case PlaceType.PLACE_TYPE_UNSPECIFIED:
    default:
      return false
  }
}

export const assetTypeEnumToString = (assetType?: AssetType): string => {
  switch (assetType) {
    case AssetType.AGRIBUSINESS:
      return "Farm/Agribusiness"
    case AssetType.AIRPORT:
      return "Airport"
    case AssetType.ASSET_TYPE_OTHER:
      return "Other"
    case AssetType.BAR_NIGHTCLUB:
      return "Bar/Nightclub"
    case AssetType.CALL_CENTER:
      return "Call Center"
    case AssetType.COMMAND_CENTER:
      return "Command Center"
    case AssetType.COMMERCIAL_KITCHEN:
      return "Commercial Kitchen"
    case AssetType.DATA_CENTER:
      return "Data Center"
    case AssetType.DISTRIBUTION_CENTER:
      return "Distribution Center"
    case AssetType.EDUCATIONAL_INSTITUTION:
      return "Educational Institution"
    case AssetType.FINANCIAL_INSTITUTION:
      return "Bank/Financial Institution"
    case AssetType.FITNESS_CENTER:
      return "Gym/Fitness Center"
    case AssetType.HOTEL:
      return "Hotel/Motel"
    case AssetType.INDOOR_EVENT_VENUE:
      return "Event Venue - Indoor"
    case AssetType.MANUFACTURING_PLANT:
      return "Manufacturing Plant/Factory"
    case AssetType.MEDICAL_CENTER:
      return "Medical Clinic/Hospital"
    case AssetType.MOVIE_THEATER:
      return "Movie Theater"
    case AssetType.OFFICE:
      return "Office Building"
    case AssetType.OUTDOOR_EVENT_VENUE:
      return "Event Venue - Outdoor"
    case AssetType.RESTAURANT_CAFE:
      return "Restaurant/Cafe"
    case AssetType.RETAIL_STORE:
      return "Retail Store"
    case AssetType.SPA_SALON:
      return "Spa/Salon"
    case AssetType.WAREHOUSE:
      return "Warehouse"
    case AssetType.ASSET_TYPE_UNSPECIFIED:
    case undefined:
    default:
      return "Unspecified"
  }
}

export const industryTypeEnumToString = (
  industryType?: IndustryType
): string => {
  switch (industryType) {
    case IndustryType.INDUSTRY_TYPE_FINANCE:
      return "Finance"
    case IndustryType.INDUSTRY_TYPE_INFORMATION:
      return "Information"
    case IndustryType.INDUSTRY_TYPE_PROFESSIONAL_SERVICES:
      return "Professional Services"
    case IndustryType.INDUSTRY_TYPE_RETAIL:
      return "Retail"
    case IndustryType.INDUSTRY_TYPE_TRANSPORTATION_AND_WAREHOUSING:
      return "Transportation and Warehousing"
    case IndustryType.INDUSTRY_TYPE_BASELINE:
      return "Baseline"
    case IndustryType.INDUSTRY_TYPE_UNSPECIFIED:
    case undefined:
    default:
      return "Unspecified"
  }
}

export const placeTypeStringToEnum = (place_type?: string): PlaceType => {
  switch (place_type) {
    case "address":
      return PlaceType.PLACE_TYPE_ADDRESS
    case "country":
      return PlaceType.PLACE_TYPE_COUNTRY
    case "district":
      return PlaceType.PLACE_TYPE_DISTRICT
    case "locality":
      return PlaceType.PLACE_TYPE_LOCALITY
    case "neighborhood":
      return PlaceType.PLACE_TYPE_NEIGHBORHOOD
    case "place":
      return PlaceType.PLACE_TYPE_PLACE
    case "poi":
      return PlaceType.PLACE_TYPE_POI
    case "postcode":
      return PlaceType.PLACE_TYPE_POSTCODE
    case "region":
      return PlaceType.PLACE_TYPE_REGION
    case undefined:
    default:
      return PlaceType.PLACE_TYPE_UNSPECIFIED
  }
}

const mapContextFieldsToMapboxLocation = (
  mapbox_location: MapboxLocation,
  fieldName: string,
  fieldValue: string
) => {
  switch (fieldName) {
    case "address":
      mapbox_location.address = fieldValue
      break
    case "country":
      mapbox_location.country = fieldValue
      break
    case "district":
      mapbox_location.district = fieldValue
      break
    case "locality":
      mapbox_location.locality = fieldValue
      break
    case "neighborhood":
      mapbox_location.neighborhood = fieldValue
      break
    case "place":
      mapbox_location.place = fieldValue
      break
    case "poi":
      mapbox_location.poi = fieldValue
      break
    case "postcode":
      mapbox_location.postcode = fieldValue
      break
    case "region":
      mapbox_location.region = fieldValue
      break
  }
}

export const convertGeocoderResultToLocation = (
  geo?: GeocoderResult
): MapboxLocation => {
  const mapbox_location: MapboxLocation = {}
  if (!geo?.result) return mapbox_location

  const { address, text, place_type, id, place_name } = geo.result
  mapbox_location.id = id
  mapbox_location.address = address ?? ""
  mapbox_location.text = text
  mapbox_location.place_type = placeTypeStringToEnum(place_type.toString())
  mapbox_location.place_name = place_name

  geo.result.context?.forEach(context => {
    const parsedContextField = context.id.split(".")[0]
    if (!parsedContextField) {
      console.error("Could not parse context field from geocoder result")
      return
    }
    mapContextFieldsToMapboxLocation(
      mapbox_location,
      parsedContextField,
      context.text
    )
  })

  return mapbox_location
}

/** Create a watched asset from location-details URL params (no Layers bucket). */
export const buildAssetFromLocationDetails = ({
  locationId,
  title,
  subtitle,
  centroid,
}: {
  locationId: string
  title?: string
  subtitle?: string
  centroid?: string
}): AssetInput => {
  const placeName = [title, subtitle].filter(Boolean).join(", ")
  return {
    asset_id: "",
    name: title ?? subtitle ?? locationId,
    threat_radius: 0,
    industry: IndustryType.INDUSTRY_TYPE_UNSPECIFIED,
    notes: "",
    asset_types: [AssetType.ASSET_TYPE_UNSPECIFIED],
    asset_groups: [],
    notification_settings: [],
    base_event: {
      geotags: [
        {
          location_id: locationId,
          wkt_centroid: centroid?.trim() || undefined,
          ...(placeName
            ? {
                mapbox_location: {
                  place_type: PlaceType.PLACE_TYPE_UNSPECIFIED,
                  place_name: placeName,
                },
              }
            : {}),
        },
      ],
    },
  }
}

export const getAssetStringToEnumMap = () => {
  const assetStringToEnumMap = new Map<string, AssetType>()

  for (const key of Object.keys(AssetType) as (keyof typeof AssetType)[]) {
    const enumValue = AssetType[key]
    const stringValue = assetTypeEnumToString(enumValue)
    assetStringToEnumMap.set(stringValue, enumValue)
  }

  return assetStringToEnumMap
}

export const assetTypeStringList = Object.values(AssetType)
  .filter(assetType => assetType !== AssetType.ASSET_TYPE_UNSPECIFIED)
  .map(assetType => assetTypeEnumToString(assetType))
  .filter(val => val !== "")
  .sort()

assetTypeStringList.unshift(
  assetTypeEnumToString(AssetType.ASSET_TYPE_UNSPECIFIED)
)

export const getIndustryStringToEnumMap = () => {
  const industryStringToEnumMap = new Map<string, IndustryType>()

  for (const key of Object.keys(
    IndustryType
  ) as (keyof typeof IndustryType)[]) {
    const enumValue = IndustryType[key]
    const stringValue = industryTypeEnumToString(enumValue)
    industryStringToEnumMap.set(stringValue, enumValue)
  }

  return industryStringToEnumMap
}

export const industryStringList = Object.values(IndustryType)
  .map(industry => industryTypeEnumToString(industry))
  .sort()

export const isGlobalAsset = (asset: { name?: string | null }) => {
  return asset.name === GLOBAL_ASSET_NAME
}

export const getNonGlobalAssets = (assets?: Asset[]) => {
  return assets?.filter(asset => !isGlobalAsset(asset)) ?? []
}

export const getGlobalAsset = (assets?: Asset[]) => {
  return assets?.find(asset => isGlobalAsset(asset))
}
